import moment from "moment"
import React, { useState } from "react"
import Chart from "react-apexcharts"
import { CardBody } from "reactstrap"
import { getWeekTooltipLabel } from "utils/dateUtil"

const TOTAL_WEEKS = 52

const toSafeNum = val => {
  const n = Number(val)
  return isNaN(n) ? null : n
}

// ── Sort Order cycling button ────────────────────────────────────────────────
const SORT_STATES = [
  { key: "chronological", icon: "⇅", label: "Sort", color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
  { key: "ascending", icon: "↑", label: "Low → High", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  { key: "descending", icon: "↓", label: "High → Low", color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
]

const SortButton = ({ sortOrder, onToggle }) => {
  const current = SORT_STATES.find(s => s.key === sortOrder) || SORT_STATES[0]
  return (
    <button
      onClick={onToggle}
      title={`Sort Order: ${current.label} — click to change`}
      style={{
        display: "flex", alignItems: "center", gap: "8px",
        background: current.bg, border: `1.5px solid ${current.border}`,
        borderRadius: "10px", padding: "5px 12px",
        cursor: "pointer", outline: "none",
        transition: "all 0.18s ease", whiteSpace: "nowrap",
        userSelect: "none", minWidth: "115px",
      }}
    >
      <span style={{ fontSize: "15px", lineHeight: 1, color: current.color, fontWeight: 700, minWidth: "16px", textAlign: "center" }}>
        {current.icon}
      </span>
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <span style={{ fontSize: "8px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: 1.1 }}>
          Sort Order
        </span>
        <span style={{ fontSize: "11px", fontWeight: 700, color: current.color, lineHeight: 1.3 }}>
          {current.label}
        </span>
      </span>
    </button>
  )
}

const LineChart = ({
  title,
  data = [],
  previousData,
  xKey,
  yKey,
  color = "#E91E63",
  fixedWeeks = false,
  yPrefix = "",
  sortOrder: sortOrderProp,
  onSortOrderChange,
}) => {
  const [localSortOrder, setLocalSortOrder] = useState("chronological")
  const sortOrder = sortOrderProp !== undefined ? sortOrderProp : localSortOrder
  const setSortOrder = onSortOrderChange ?? setLocalSortOrder

  const handleToggle = () => {
    const next =
      sortOrder === "chronological" ? "ascending"
        : sortOrder === "ascending" ? "descending"
          : "chronological"
    setSortOrder(next)
  }

  const safeData = data ?? []
  const hasPrev = !!previousData?.length

  let series, allDates, prevDates, categories, currentYear, previousYear

  if (hasPrev) {
    // ISO week union — aligns both years by week number
    const weekSet = new Set()
    safeData.forEach(item => weekSet.add(moment(item[xKey]).isoWeek()))
    previousData.forEach(item => weekSet.add(moment(item[xKey]).isoWeek()))
    const allWeeks = Array.from(weekSet).sort((a, b) => a - b)

    const currentMap = {}
    safeData.forEach(item => {
      currentMap[moment(item[xKey]).isoWeek()] = toSafeNum(item[yKey])
    })
    const prevMap = {}
    previousData.forEach(item => {
      prevMap[moment(item[xKey]).isoWeek()] = toSafeNum(item[yKey])
    })

    const dateByWeek = {}
    safeData.forEach(item => {
      dateByWeek[moment(item[xKey]).isoWeek()] = moment(item[xKey]).format(
        "DD-MM-YYYY"
      )
    })
    const prevDateByWeek = {}
    previousData.forEach(item => {
      prevDateByWeek[moment(item[xKey]).isoWeek()] = moment(item[xKey]).format(
        "DD-MM-YYYY"
      )
    })

    currentYear = safeData.length
      ? moment(safeData[0][xKey]).year()
      : moment().year()
    previousYear = previousData.length
      ? moment(previousData[0][xKey]).year()
      : currentYear - 1

    // ── Apply Sort Order ──
    let displayWeeks = [...allWeeks]
    if (sortOrder === "ascending") {
      displayWeeks.sort((a, b) => {
        const vA = currentMap[a] ?? 0
        const vB = currentMap[b] ?? 0
        return vA !== vB ? vA - vB : a - b
      })
    } else if (sortOrder === "descending") {
      displayWeeks.sort((a, b) => {
        const vA = currentMap[a] ?? 0
        const vB = currentMap[b] ?? 0
        return vA !== vB ? vB - vA : a - b
      })
    }

    categories = displayWeeks.map((_, i) => `${i + 1}`)
    allDates = displayWeeks.map(w => dateByWeek[w] || null)
    prevDates = displayWeeks.map(w => prevDateByWeek[w] || null)

    series = [
      {
        name: `${currentYear}`,
        data: displayWeeks.map(w => currentMap[w] ?? null),
      },
      { name: `${previousYear}`, data: displayWeeks.map(w => prevMap[w] ?? null) },
    ]
  } else if (fixedWeeks) {
    const weekValues = new Array(TOTAL_WEEKS).fill(null)
    const weekDates = new Array(TOTAL_WEEKS).fill(null)
    safeData.forEach(item => {
      const weekNum = moment(item[xKey]).isoWeek()
      const idx = weekNum - 1
      if (idx >= 0 && idx < TOTAL_WEEKS) {
        weekValues[idx] = toSafeNum(item[yKey])
        weekDates[idx] = moment(item[xKey]).format("DD-MM-YYYY")
      }
    })

    let indices = Array.from({ length: TOTAL_WEEKS }, (_, i) => i)
    if (sortOrder === "ascending") {
      indices.sort((a, b) => (weekValues[a] ?? 0) - (weekValues[b] ?? 0))
    } else if (sortOrder === "descending") {
      indices.sort((a, b) => (weekValues[b] ?? 0) - (weekValues[a] ?? 0))
    }

    allDates = indices.map(i => weekDates[i])
    prevDates = []
    categories = indices.map((_, i) => `${i + 1}`)
    series = [{ name: title, data: indices.map(i => weekValues[i]) }]
  } else {
    let sortedItems = [...safeData].sort((a, b) => new Date(a[xKey]) - new Date(b[xKey]))
    if (sortOrder === "ascending") {
      sortedItems.sort((a, b) => (toSafeNum(a[yKey]) ?? 0) - (toSafeNum(b[yKey]) ?? 0))
    } else if (sortOrder === "descending") {
      sortedItems.sort((a, b) => (toSafeNum(b[yKey]) ?? 0) - (toSafeNum(a[yKey]) ?? 0))
    }

    allDates = sortedItems.map(item => moment(item[xKey]).format("DD-MM-YYYY"))
    prevDates = []
    categories = sortedItems.map((_, i) => `${i + 1}`)
    series = [
      { name: title, data: sortedItems.map(item => toSafeNum(item[yKey])) },
    ]
  }

  const visibleCategories = categories
  const visibleSeries = series
  const visibleDates = allDates
  const visiblePrevDates = prevDates

  const options = {
    chart: {
      type: "area",
      toolbar: { show: false },
      zoom: { enabled: false },
      background: "transparent",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 400,
        animateGradually: { enabled: true, delay: 100 },
      },
      dropShadow: {
        enabled: !hasPrev,
        color,
        top: 6,
        blur: 10,
        opacity: 0.2,
      },
    },
    stroke: hasPrev
      ? { curve: "smooth", width: [3, 2], dashArray: [0, 6] }
      : { curve: "smooth", width: 3, colors: [color] },
    markers: {
      size: hasPrev ? [4, 3] : 4,
      colors: ["#fff"],
      strokeColors: hasPrev ? [color, "#94a3b8"] : [color],
      strokeWidth: hasPrev ? [3, 2] : 3,
      hover: { size: 7 },
    },
    dataLabels: { enabled: false },
    colors: hasPrev ? [color, "#94a3b8"] : [color],
    fill: hasPrev
      ? {
        type: "gradient",
        gradient: {
          type: "vertical",
          opacityFrom: [0.35, 0],
          opacityTo: [0.02, 0],
          stops: [0, 100],
        },
      }
      : {
        type: "gradient",
        gradient: {
          type: "vertical",
          shadeIntensity: 0,
          gradientToColors: [color],
          inverseColors: false,
          opacityFrom: 0.4,
          opacityTo: 0.02,
          stops: [0, 95, 100],
        },
      },
    xaxis: {
      categories: visibleCategories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
      title: {
        text: "Week",
        style: { color: "#94a3b8", fontSize: "13px", fontWeight: 600 },
      },
    },
    yaxis: {
      labels: {
        style: { colors: "#94a3b8", fontSize: "11px" },
        formatter: val =>
          val == null ? "N/A" : `${yPrefix}${val.toLocaleString("en-GB")}`,
      },
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 16, bottom: 0, left: 8 },
    },
    tooltip: {
      theme: "dark",
      shared: hasPrev,
      intersect: false,
      x: {
        formatter: (_, { dataPointIndex }) => {
          if (!hasPrev)
            return getWeekTooltipLabel(dataPointIndex, visibleDates[dataPointIndex])
          const curLabel = getWeekTooltipLabel(
            dataPointIndex,
            visibleDates[dataPointIndex]
          )
          const prevLabel = getWeekTooltipLabel(
            dataPointIndex,
            visiblePrevDates[dataPointIndex]
          )
          return `${currentYear}: ${curLabel || "N/A"}<br/>${previousYear}: ${prevLabel || "N/A"
            }`
        },
      },
      y: {
        formatter: val =>
          val == null ? "N/A" : `${yPrefix}${val.toLocaleString("en-GB")}`,
      },
    },
    legend: hasPrev
      ? {
        show: true,
        position: "top",
        horizontalAlign: "right",
        fontSize: "11px",
        fontWeight: 600,
        markers: { radius: 3 },
      }
      : { show: false },
  }

  return (
    <div
      style={{
        background: "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
        borderRadius: "16px",
        border: `1px solid ${color}22`,
        boxShadow: `0 4px 24px ${color}14, 0 1px 4px rgba(0,0,0,0.04)`,
        position: "relative",
        overflow: "hidden",
        marginBottom: "1.5rem",
      }}
    >
      {/* Top accent bar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${color}, ${color}88)`,
          borderRadius: "16px 16px 0 0",
        }}
      />
      <CardBody style={{ padding: "20px 16px 8px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "4px",
            padding: "0 8px",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: color,
                boxShadow: `0 0 8px ${color}88`,
              }}
            />
            <h4
              style={{
                margin: 0,
                fontSize: "13px",
                fontWeight: 700,
                color: "#1e293b",
                letterSpacing: "-0.2px",
              }}
            >
              {title}
            </h4>
          </div>

          {/* Sort Order cycling button */}
          <SortButton sortOrder={sortOrder} onToggle={handleToggle} />
        </div>
        <Chart options={options} series={visibleSeries} type="area" height={320} />
      </CardBody>
    </div>
  )
}

export default LineChart
